import {
  observable,
  action,
  reaction,
  keys,
  computed,
  makeObservable
} from 'mobx';
import { debounce } from 'debounce';
import agent from '../lib/agent';
import ProjectStore from './ProjectStore';
import MapStore from './MapStore';
import {
  formatBuildingData,
  formatBuildingPower,
  formatBuildingDetails
} from '../lib/formatData';

const parseFilters = filters => {
  const q = {
    filters: {}
  };

  Object.keys(filters).forEach(filterName => {
    const filterValue = filters[filterName];
    if (filterValue === null || filterValue === '') {
      return;
    }

    if (filterName === 'geo_polygon') {
      q.filters[filterName] = filterValue;
      return;
    }

    if (typeof filterValue === 'object') {
      if (filterName.match(/^estimates_int/i)) {
        if (filterValue.start) {
          q.filters[`${filterName}.low`] = {
            gte: filterValue.start
          };
        }

        if (filterValue.end) {
          q.filters[`${filterName}.high`] = {
            lte: filterValue.end
          };
        }
      } else if (filterName.match(/^incentives/i)) {
        q.filters[filterName] = filterValue;
      } else if (filterName === 'radius') {
        q.filters.radius = filterValue;
      } else if (filterName === 'sort') {
        q.sort = filterValue;
      } else {
        q.filters[filterName] = {
          gte: filterValue.start,
          lte: filterValue.end
        };
      }
    } else if (!['skip', 'limit', 'favourites'].includes(filterName)) {
      q.filters[filterName] = filterValue;
    } else {
      q[filterName] = filterValue;
    }
  });

  return q;
};

class Store {
  buildings = new Map();
  activeProject = null;
  aggregations = null;
  emissions = null;
  financials = null;
  filters = {};
  projectId = null;
  sort = '';

  pickedResults = new Set();
  savedSearches = [];
  totalResults = {
    value: 0,
    relation: 'gte'
  };
  hexagons = [];
  points = [];
  bbox = null;
  location = null;

  resultMap = new Map();

  setResults(results) {
    this.resultMap = results;
  }

  updateBuildingName(buildingId, buildingName) {
    const building = this.resultMap.get(buildingId);
    const updatedBuilding = { ...building, building_name: buildingName };
    this.resultMap.set(buildingId, updatedBuilding);
    const newResults = new Map(this.resultMap);
    newResults.set(buildingId, updatedBuilding);
    this.resultMap = newResults;
  }

  loadResult = async ids => {
    if (!ids.length) {
      return;
    }

    // Set state to loading
    ids.forEach(id => {
      this.resultMap.set(id, 1);
    });

    const { data } = await agent.Building.getBuildingsById(ids);

    data.data.forEach(item => {
      this.resultMap.set(item.id, item);
    });

    return data;
  };

  debounceLoadResult = debounce(this.loadResult, 500);

  get = id => {
    return this.resultMap.get(id);
  };

  pickAllResults = () => {
    this.pickedResults = new Set([...this.resultMap.keys()]);
  };

  pickAllFeatures = features => {
    this.pickedResults = new Set(features.map(feature => feature.id));
  };

  createProjectFromPicked = async params => {
    const pickedResults = [...this.pickedResults];
    if (!pickedResults.size) return;
    this.clearPicked();

    const project = await ProjectStore.createProject({
      ...params,
      pickedResults
    });
    ProjectStore.fetchAll();

    return project;
  };

  setSort = value => {
    const selected = this.sort && this.sort.includes(value);
    if (selected && this.sort.includes('desc')) {
      this.sort = `${value}:asc`;
    } else {
      this.sort = `${value}:desc`;
    }
    return this.sort;
  };

  constructor() {
    makeObservable(this, {
      projectId: observable,
      buildings: observable,
      activeProject: observable,
      aggregations: observable,
      filters: observable,
      financials: observable,
      emissions: observable,
      sort: observable,
      pickedResults: observable,
      savedSearches: observable,
      totalResults: observable,
      hexagons: observable,
      points: observable,
      bbox: observable,
      location: observable,
      resultMap: observable,
      loadResult: action,
      debounceLoadResult: action,
      get: action,
      pickAllResults: action,
      pickAllFeatures: action,
      createProjectFromPicked: action,
      setSort: action,
      clearPicked: action,
      pickResult: action,
      unPickResult: action,
      setFilter: action,
      clearFilters: action,
      getFilterValue: action,
      filtersCount: computed,
      fetchBuilding: action,
      clearAllFilters: action,
      setFilters: action,
      restoreFilters: action,
      saveFilters: action,
      deleteSavedSearch: action,
      getSavedSearches: action,
      fetch: action,
      loadBuilding: action,
      getWeather: action,
      getPlaces: action,
      getPowerOptions: action,
      params: computed,
      updateBuildingName: action
    });
  }

  clearPicked() {
    this.pickedResults = new Set();
  }

  pickResult(id) {
    console.log('pick: pickResult', id);
    this.pickedResults.add(id);
  }

  unPickResult(id) {
    this.pickedResults.delete(id);
  }

  setFilter = (name, value) => {
    const newFilters = { ...this.filters };
    if (value === null) {
      delete newFilters[name];
    } else {
      newFilters[name] = value;
    }
    this.filters = newFilters;
  };

  clearFilters = () => {
    this.filters = {};
    this.sort = '';
  };

  getFilterValue(name) {
    return this.filters[name] || null;
  }

  get filtersCount() {
    return keys(this.filters).length;
  }

  get params() {
    return parseFilters(this.filters);
  }

  fetchBuilding = async id => {
    const { data } = await agent.Building.getBuildingsById([id], 1);

    return data.data[0];
  };

  clearAllFilters() {
    this.filters = {};
  }

  setFilters = newFilters => {
    this.filters = newFilters;
  };

  restoreFilters = async savedSearchId => {
    const response = await agent.SavedSearchFilters.get(savedSearchId);
    this.setFilters(response.data?.data?.query);
  };

  saveFilters = async (name, projectId) => {
    const response = await agent.SavedSearchFilters.create(
      name,
      this.filters,
      projectId
    );
    const newSavedSearches = [...this.savedSearches, response.data.data];
    this.savedSearches = newSavedSearches;
  };

  deleteSavedSearch = async savedSearchId => {
    const newSavedSearches = [...this.savedSearches].filter(
      savedSearch => savedSearch.id !== savedSearchId
    );

    this.savedSearches = newSavedSearches;

    agent.SavedSearchFilters.delete(savedSearchId);
  };

  getSavedSearches = async projectId => {
    const response = await agent.SavedSearchFilters.getAll({
      projectId
    });
    this.savedSearches = response.data.data;
  };

  fetch = async () => {
    this.results = [];
    this.points = [];
    // Fill results with loading results for visual feedback
    const arr = [...new Array(10)].map((item, index) => {
      return [index, 0];
    });
    this.resultMap = new Map(arr);

    const params = parseFilters(this.filters);

    if (this.sort) {
      params.sort = this.sort;
    }

    params.version = 'v2';

    const { data } = await agent.Building.getBuildings(params);

    const map = data.data.reduce((acc, result) => {
      acc[result.id] = result;
      return acc;
    }, {});

    if (data.ids) {
      const arr = data.ids.map(value => {
        const [id] = value;
        if (map[id]) {
          return [id, map[id]];
        }
        return [id, 0];
      });

      this.resultMap = new Map(arr);
    }

    this.location = data.location || null;

    if (data.hexagons?.length) {
      this.hexagons = data.hexagons;
    } else {
      this.hexagons = [];
    }

    // Don't change the order since observers depend on it
    this.bbox = data.bbox;

    if (!this.filters.geo_polygon) {
      MapStore.setBounds(data.bbox);
    }

    if (this.filters.project_id) {
      this.projectId = this.filters.project_id;
    }

    this.points = data.ids;
    this.financials = data.financials;
    this.emissions = data.emissions;

    this.totalResults = data.total;
    this.aggregations = data.aggregations;
  };

  loadBuilding = async buildingId => {
    const { data } = await agent.Building.getBuilding(buildingId);

    if (data.data) {
      const formattedData = formatBuildingData(data.data);
      const building = {
        ...formattedData,
        formatted_details: data.data.formatted_details,
        details: formatBuildingDetails(data.data)
      };

      // console.log('BUILDING', formattedData, data.data);

      this.buildings.set(data.data.id, building);

      return building;
    }
    return null;
  };

  getWeather = async (buildingId, lon, lat) => {
    const { data } = await agent.Building.getWeather(lon, lat);
    if (data?.data) {
      const weather = data.data.wind_speed_temp.reduce(
        (acc, item) => {
          acc.weather_timestamp.push(item[0] * 1000);
          acc.wind_speed.push(item[1]);
          acc.temperature.push(item[2]);
          return acc;
        },
        {
          weather_timestamp: [],
          wind_speed: [],
          temperature: []
        }
      );
      const building = this.buildings.get(buildingId);
      this.buildings.set(buildingId, {
        ...building,
        ...weather
      });
    }
  };

  getPowerOptions = async (buildingId, lon, lat) => {
    const { data } = await agent.Building.getPowerOptions(lon, lat);
    if (data?.data) {
      const {
        power_line_closest_point,
        power_line_geometry,
        power_line_id,
        power_plant_geometry
      } = data.data;
      const building = this.buildings.get(buildingId);
      this.buildings.set(buildingId, {
        ...building,
        details: {
          ...building.details,
          ...formatBuildingPower(data.data)
        },
        closest_power_plant: true,
        power_line: {
          power_line_closest_point,
          power_line_geometry,
          power_line_id,
          power_plant_geometry
        }
      });
    }
  };

  getPlaces = async (buildingId, lon, lat) => {
    const { data } = await agent.Building.getPlaces(lon, lat);
    if (data?.data) {
      const building = this.buildings.get(buildingId);
      this.buildings.set(buildingId, {
        ...building,
        places: data.data
      });
    }
  };
}

const store = new Store();

reaction(
  () => JSON.stringify([store.filters]),
  debounce(() => {
    if (
      (Object.keys(store.filters).includes('addresses') &&
        store.filters.addresses.length) === 0 &&
      Object.keys(store.filters).length === 1
    ) {
      return;
    }
    store.fetch();
  }, 350)
);

export default store;
